import { OnDemandCardData,  ProgramCardData, ChannelLiveCardData } from "@/components/Cards/types";
import { AppConstants } from "@/common";
import { useExtraCards } from "@/composables/useExtraCards";
import { useFavoriteOnDemandsStore } from "@/stores/FavoritesOnDemand";
import { useFavoriteProgramsStore } from "@/stores/FavoritesPrograms";
import { useFavoriteChannelsStore } from "@/stores/FavoriteChannels";
import { useFavorite } from "@/composables/useFavorite";
import type { ExposeInterface } from './types';
import type { Channel, ProgramGuideItem } from '@ca.vmedia.tv/domains';
import type { AxiosError } from "axios";

export const useLinesMyTvStore = defineStore('LinesMyTv', (): ExposeInterface => {
    const { deleteSavedIdsFavorites } = useFavorite();

    const FavoriteChannels = useFavoriteChannelsStore();
    const {
        getFavoriteChannels: _favoriteChannels
    } = storeToRefs(FavoriteChannels);
    const { 
        fetchFavoriteChannels,
        clearHard: clearHardFavoriteChannels 
    } = FavoriteChannels;


    const FavoriteOnDemandsStore = useFavoriteOnDemandsStore();
    const {
        getFavoriteOnDemands: _favoritesOnDemand
    } = storeToRefs(FavoriteOnDemandsStore);
    const { 
        fetchFavoriteOnDemands,
        clearHard: clearHardFavoriteOnDemands 
    } = FavoriteOnDemandsStore;


    const FavoriteProgramsStore = useFavoriteProgramsStore();
    const {
        getFavoritePrograms: _favoritePrograms
    } = storeToRefs(FavoriteProgramsStore);
    const { 
        fetchFavoritePrograms,
        clearHard: clearHardFavoritePrograms 
    } = FavoriteProgramsStore;


    const { hasShowAll, getCardShowAll, getCardPlus } = useExtraCards();


    const getCardsChannels = computed((): ChannelLiveCardData[] | null => {
        const data = _favoriteChannels.value;

        if (data === null || data.items === null) {
            return null;
        }

        const cardPlus = getCardPlus("program-card", 'MY_TV_ADD_FAVORITE_CHANNELS');
        if (data.items.length === 0) {
            return [...cardPlus] as ChannelLiveCardData[];
        }

        let items = (data.items).slice(0, AppConstants.ItemsInLine).map((item) => new ChannelLiveCardData(item as unknown as Channel));
        
        if (hasShowAll(data.paging)) {
            items = [...items, ...getCardShowAll(items, data.paging)] as ChannelLiveCardData[];
        }
        
        return [...items, ...cardPlus] as ChannelLiveCardData[];
    });
    const fetchMyTvChannels = async (): Promise<void> => {
        try {
            if (_favoriteChannels.value !== null) {
                return;
            }
            await deleteSavedIdsFavorites();
            
            await fetchFavoriteChannels();
        } catch (error) {
            Logger.captureError(error as Error | AxiosError);
        }
    };

    
    const getCardsOnDemand = computed((): OnDemandCardData[] | null => {
        const data = _favoritesOnDemand.value;

        if (data === null || data.items === null) {
            return null;
        }

        const cardPlus = getCardPlus("ondemand-card", 'MY_TV_SEE_ON_DEMANDS');
        if (data.items.length === 0) {
            return [...cardPlus] as OnDemandCardData[];
        }
        
        let items = data.items.slice(0, AppConstants.ItemsInLine).map((item) => new OnDemandCardData(item));

        if (hasShowAll(data.paging)) {
            items = [...items, ...getCardShowAll(items, data.paging)] as OnDemandCardData[];
        }
        return [...items, ...cardPlus] as OnDemandCardData[];
    }); 
    const fetchMyTvOnDemand = async (): Promise<void> => {
        try {
            if (_favoritesOnDemand.value !== null) {
                return;
            }
            await deleteSavedIdsFavorites();
            
            await fetchFavoriteOnDemands();
        } catch (error) {
            Logger.captureError(error as Error | AxiosError);
        }
    };

    const getCardsPrograms = computed((): ProgramCardData[] | null => {
        const data = _favoritePrograms.value

        if (data === null || data.items === null) {
            return null;
        }

        const cardPlus = getCardPlus("program-card", 'MY_TV_SEE_PROGRAMS_IN_GUIDE');
        if (data.items.length === 0) {
            return [...cardPlus] as ProgramCardData[];
        }

        let items = data.items.slice(0, AppConstants.ItemsInLine).map((item: ProgramGuideItem) => new ProgramCardData(item));
        
        if (hasShowAll(data.paging)) {
            items = [...items, ...getCardShowAll(items, data.paging)] as ProgramCardData[];
        }
        
        return [...items, ...cardPlus] as ProgramCardData[];
    });
    const fetchMyTvPrograms = async (): Promise<void> => {
        try {
            if (_favoritePrograms.value !== null) {
                return;
            }
            await deleteSavedIdsFavorites();
            await fetchFavoritePrograms();
        } catch (error) {
            Logger.captureError(error as Error | AxiosError);
        }
    };


    const _counterRows: Ref<number> = ref(AppConstants.HomeCounterLines);
    const getCounterLinesMyTv = computed((): number => _counterRows.value);
    const _clearCounter = (): void => {
        _counterRows.value = AppConstants.HomeCounterLines;
    };
    const increaseCounterLinesMyTv = (): void => {
        _counterRows.value += 1;
    };

    const clearAll = (): void => {
        _clearCounter();
        clearHardFavoriteOnDemands();
        clearHardFavoritePrograms();
        clearHardFavoriteChannels();
    };


    return {
        getCardsOnDemand,
        getCardsPrograms,
        getCardsChannels,
        fetchMyTvChannels,
        fetchMyTvOnDemand,
        fetchMyTvPrograms,
        getCounterLinesMyTv,
        increaseCounterLinesMyTv,
        clearAll
    };
});
